import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, KeyRound, CheckCircle, Mail, Phone } from 'lucide-react';
import { sendPasswordResetEmail } from 'firebase/auth';
import { useTranslation } from 'react-i18next';
import { auth } from '@/shared/lib/firebase';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { useToast } from '@/shared/hooks/use-toast';
import { AuthLayout } from '../layouts/AuthLayout';
import { AuthFormLayout } from '../layouts/AuthFormLayout';
import { LoadingButton } from '../components/LoadingButton';
import { PhoneAuthFlow } from '../components/PhoneAuthFlow';

const ForgotPassword = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [mode, setMode] = useState<'email' | 'phone'>('email');
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setIsLoading(true);
    try {
      await sendPasswordResetEmail(auth, email.trim());
      setSent(true);
    } catch (err: unknown) {
      const code = (err as { code?: string })?.code ?? '';
      if (code === 'auth/user-not-found' || code === 'auth/invalid-email') {
        setSent(true); // don't leak whether the email is registered
      } else {
        toast({ title: t('common.error'), description: t('auth.forgotPassword.errors.failed'), variant: 'destructive' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handlePhoneSuccess = (_isNewUser: boolean, needsProfileCompletion: boolean) => {
    navigate(needsProfileCompletion ? '/complete-profile' : '/reset-password', { replace: true });
  };

  const handlePhoneError = (message: string) => {
    toast({ title: t('common.error'), description: message, variant: 'destructive' });
  };

  if (sent) {
    return (
      <AuthLayout showBranding={false}>
        <AuthFormLayout title={t('auth.forgotPassword.emailSent')} subtitle="">
          <div className="text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center mx-auto">
              <CheckCircle className="w-8 h-8 text-success" />
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {t('auth.forgotPassword.emailSentDesc', { email })}{' '}
              {t('auth.forgotPassword.emailSentSpam')}
            </p>
            <Link to="/login" className="inline-flex items-center gap-2 text-accent font-medium hover:underline">
              {t('auth.forgotPassword.backToLogin')}
            </Link>
          </div>
        </AuthFormLayout>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout showBranding={false}>
      <AuthFormLayout
        title={t('auth.forgotPassword.title')}
        subtitle={mode === 'email' ? t('auth.forgotPassword.subtitle') : t('auth.forgotPassword.phoneSubtitle')}
      >
        {/* Email / Phone toggle */}
        <div className="flex rounded-lg border border-border p-1 gap-1 bg-muted/30">
          <button
            type="button"
            onClick={() => setMode('email')}
            className={`flex-1 flex items-center justify-center gap-2 rounded-md py-2 text-sm font-medium transition-colors ${
              mode === 'email' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Mail className="w-4 h-4" />
            {t('common.email')}
          </button>
          <button
            type="button"
            onClick={() => setMode('phone')}
            className={`flex-1 flex items-center justify-center gap-2 rounded-md py-2 text-sm font-medium transition-colors ${
              mode === 'phone' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Phone className="w-4 h-4" />
            {t('common.phone')}
          </button>
        </div>

        {mode === 'email' ? (
          <form onSubmit={handleEmailSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="fp-email">{t('common.email')}</Label>
              <Input
                id="fp-email"
                type="email"
                placeholder={t('auth.forgotPassword.emailPlaceholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-12 input-elegant"
                required
                autoFocus
              />
            </div>
            <LoadingButton
              type="submit"
              isLoading={isLoading}
              loadingText={t('auth.forgotPassword.sending')}
              icon={KeyRound}
              disabled={!email.trim()}
            >
              {t('auth.forgotPassword.submit')}
            </LoadingButton>
          </form>
        ) : (
          <PhoneAuthFlow
            submitLabel={t('auth.forgotPassword.verifyPhone')}
            onSuccess={handlePhoneSuccess}
            onError={handlePhoneError}
          />
        )}

        <Link
          to="/login"
          className="flex items-center justify-center gap-2 text-sm text-muted-foreground hover:text-foreground mt-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('auth.forgotPassword.backToLogin')}
        </Link>
      </AuthFormLayout>
    </AuthLayout>
  );
};

export default ForgotPassword;
